import { Shield, ExternalLink } from 'lucide-react';

const ChromeIcon = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5 flex-shrink-0" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="10" fill="#4285F4"/>
    <circle cx="24" cy="24" r="6" fill="white"/>
    <circle cx="24" cy="24" r="4" fill="#4285F4"/>
    <path d="M24 14 L44 14 A20 20 0 0 1 34.64 31.5 L24 14Z" fill="#EA4335"/>
    <path d="M24 14 L13.36 31.5 A20 20 0 0 1 4 14 L24 14Z" fill="#FBBC04"/>
    <path d="M13.36 31.5 L34.64 31.5 A20 20 0 0 1 4 14 M13.36 31.5Z" fill="transparent"/>
    <path d="M24 34 A20 20 0 0 1 4 14" fill="none"/>
    <path d="M34.64 31.5 A20 20 0 0 1 13.36 31.5 L24 44 Z" fill="#34A853"/>
  </svg>
);

const PlayStoreBadge = () => (
  <svg viewBox="0 0 135 40" xmlns="http://www.w3.org/2000/svg" className="h-9 w-auto">
    <rect width="135" height="40" rx="5" fill="#000"/>
    <text x="44" y="13" fill="white" fontFamily="Arial, sans-serif" fontSize="7" letterSpacing="0.5">GET IT ON</text>
    <text x="44" y="27" fill="white" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="bold" letterSpacing="0.3">Google Play</text>
    <g transform="translate(10,8)">
      <path d="M1.5 0.5 L18.5 11 L1.5 21.5 Z" fill="#4285F4"/>
      <path d="M1.5 0.5 L10 11 L1.5 21.5" fill="#34A853"/>
      <path d="M1.5 0.5 L10 11 L18.5 11" fill="#FBBC04"/>
      <path d="M10 11 L18.5 11 L1.5 21.5" fill="#EA4335"/>
    </g>
  </svg>
);

const AppStoreBadge = () => (
  <svg viewBox="0 0 135 40" xmlns="http://www.w3.org/2000/svg" className="h-9 w-auto">
    <rect width="135" height="40" rx="5" fill="#000"/>
    <text x="38" y="13" fill="white" fontFamily="Arial, sans-serif" fontSize="7" letterSpacing="0.5">Download on the</text>
    <text x="38" y="27" fill="white" fontFamily="Arial, sans-serif" fontSize="15" fontWeight="bold" letterSpacing="0.3">App Store</text>
    <g transform="translate(10,7)">
      <path d="M13 2 C13 2 9 2 8 6 L6 10 C5 12 5 14 6 16 L12 26 C13 28 15 28 16 26 L18 22 L10 22 L12 18 L20 18 L24 26 C25 28 27 28 28 26 L30 22 C31 20 31 18 30 16 L20 2 C19 0 17 0 16 2 Z" fill="white" transform="scale(0.7) translate(-2,0)"/>
    </g>
  </svg>
);

const downloads = [
  {
    label: 'Chrome for Desktop',
    sub: 'Windows & Linux',
    url: 'https://www.google.com/chrome/',
    badge: null,
    isStore: false,
  },
  {
    label: 'Chrome on Android',
    sub: null,
    url: 'https://play.google.com/store/apps/details?id=com.android.chrome',
    badge: <PlayStoreBadge />,
    isStore: true,
  },
  {
    label: 'Chrome on iPhone',
    sub: null,
    url: 'https://apps.apple.com/us/app/google-chrome/id535886823',
    badge: <AppStoreBadge />,
    isStore: true,
  },
  {
    label: 'Chrome for macOS',
    sub: 'Mac download',
    url: 'https://www.google.com/chrome/mac/',
    badge: null,
    isStore: false,
  },
];

export default function AuthPlatformNotice() {
  return (
    <div className="w-full max-w-2xl mx-auto px-4 pb-10">
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        {/* Header bar */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-gray-100">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Shield className="w-4 h-4 text-blue-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Recommended Access &amp; Platform Support</p>
            <p className="text-xs text-gray-500 mt-0.5">Best experienced on Google Chrome — Desktop, Android, iPhone, or macOS</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <p className="text-xs text-gray-500 leading-relaxed mb-4">
            This platform is currently receiving issuer rendering updates. If pages appear blank or slow,
            installing the latest version of <span className="font-medium text-gray-700">Google Chrome</span> typically resolves the issue.
          </p>

          {/* Download grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {downloads.map(({ label, sub, url, badge, isStore }) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 px-3.5 py-3 transition-all group"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <ChromeIcon />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate leading-tight">{label}</p>
                    {sub && <p className="text-[10px] text-gray-400 leading-tight mt-0.5">{sub}</p>}
                  </div>
                </div>
                {isStore ? (
                  <div className="flex-shrink-0">{badge}</div>
                ) : (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-[10px] text-gray-400 hidden sm:block">Download</span>
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 transition-colors" />
                  </div>
                )}
              </a>
            ))}
          </div>

          <p className="text-[10px] text-gray-400 mt-3 text-center">
            Supported browsers and rendering updates are actively being optimized.
          </p>
        </div>
      </div>
    </div>
  );
}
